import React from "react";
import { Link } from "react-router-dom";

/**
 * Colour band — Cohere-inspired chromatic panels for the marketing home.
 * Kept in a single component so it can be removed by flipping the
 * SHOW_COLOUR_BAND flag in Home.jsx. Adds NO structural changes to any
 * existing section.
 *
 * Three stacked panels:
 *   1. Deep navy immersive statement + CTA (violet-family, grain, chroma)
 *   2. Warm sunset testimonial (portrait quote + a chromatic sky panel)
 *   3. Green pastoral "sovereign infrastructure" panel with a floating dark
 *      terminal card overlay.
 */

const NEWS_CARDS = [
  {
    id: "cbn-2027",
    tag: "Regulation",
    date: "Feb 12, 2026",
    title: "What the January 2027 CBN residency deadline actually asks of AI systems",
    tint: "seafoam",
    to: "/enterprise-2/research",
  },
  {
    id: "sovereign-stack",
    tag: "Product",
    date: "Feb 04, 2026",
    title: "One sovereign stack, top to bottom: how Crowther owns the whole path from compute to agents",
    tint: "navy",
    to: "/enterprise-2/solution",
  },
  {
    id: "sagicor-realloc",
    tag: "Deployment",
    date: "Jan 21, 2026",
    title: "Realloc across eight operations of a leading multinational finance company: what a real Caribbean deployment looks like",
    tint: "emerald",
    to: "/enterprise-2/blog",
  },
];

export function CohereBand() {
  return (
    <>
      {/* ================================================================
          Panel 1 — Chromatic immersive statement
          ================================================================ */}
      <section
        className="e2-cband e2-cband-statement"
        data-testid="e2-cband-statement"
        aria-label="Ready to build sovereign AI"
      >
        <div className="e2-cband-statement-inner">
          <div className="e2-cband-eyebrow">The Crowther Deployment</div>
          <h2 className="e2-cband-statement-title">
            Ready to bring your intelligence&nbsp;home?
          </h2>
          <p className="e2-cband-statement-sub">
            One stack, deployed in-country, on infrastructure your firm owns. Book a working session and we will walk you through what your first ninety days would look like.
          </p>
          <div className="e2-cband-statement-actions">
            <Link
              to="/plan-2"
              className="e2-cband-btn e2-cband-btn-primary"
              data-testid="e2-cband-statement-cta"
            >
              Get your firm&rsquo;s customised AI plan
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <Link
              to="/enterprise-2/contact"
              className="e2-cband-btn e2-cband-btn-ghost"
              data-testid="e2-cband-statement-secondary"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================
          Panel 2 — Testimonial with a warm sunset skyline
          ================================================================ */}
      <section
        className="e2-cband e2-cband-testimonial"
        data-testid="e2-cband-testimonial"
        aria-label="Why leading teams trust Crowther"
      >
        <div className="e2-shell e2-cband-testimonial-inner">
          <div className="e2-cband-testimonial-head">
            <h3 className="e2-cband-testimonial-heading">
              Why serious institutions choose to work with us
            </h3>
          </div>

          <div className="e2-cband-testimonial-grid">
            <article
              className="e2-cband-quote-card"
              data-testid="e2-cband-quote-card"
            >
              <div className="e2-cband-quote-brand">LEADING MULTINATIONAL FINANCE COMPANY</div>
              <blockquote className="e2-cband-quote">
                &ldquo;Realloc runs where our customers live. Our data does not leave the jurisdiction it must stay inside, and the whole stack is a system we can hand our regulator on demand.&rdquo;
              </blockquote>
              <div className="e2-cband-quote-attrib">
                A programme lead at a leading multinational finance company, on running Realloc across the Caribbean, North and Central America.
              </div>
              <Link
                to="/enterprise-2/blog"
                className="e2-cband-read-more"
                data-testid="e2-cband-quote-read-more"
              >
                Read more
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </article>

            <div
              className="e2-cband-skyline"
              data-testid="e2-cband-skyline"
              aria-hidden
            >
              <div className="sky" />
              <div className="sun" />
              <div className="grain" />
              <svg
                className="skyline-svg"
                viewBox="0 0 800 260"
                preserveAspectRatio="xMidYMax slice"
                aria-hidden
              >
                {/* Silhouette skyline — pure vector, no external images */}
                <g fill="rgba(22,30,27,0.86)">
                  <rect x="0"   y="150" width="60"  height="110" />
                  <rect x="55"  y="120" width="34"  height="140" />
                  <rect x="88"  y="90"  width="46"  height="170" />
                  <rect x="130" y="140" width="28"  height="120" />
                  <rect x="158" y="110" width="42"  height="150" />
                  <rect x="200" y="70"  width="60"  height="190" />
                  <polygon points="230,50 240,50 235,10 235,10" fill="rgba(22,30,27,0.86)" />
                  <rect x="260" y="130" width="30"  height="130" />
                  <rect x="290" y="100" width="52"  height="160" />
                  <rect x="342" y="80"  width="38"  height="180" />
                  <rect x="378" y="60"  width="70"  height="200" />
                  <polygon points="410,40 418,40 414,4 414,4" />
                  <rect x="448" y="120" width="26"  height="140" />
                  <rect x="472" y="90"  width="46"  height="170" />
                  <rect x="518" y="130" width="34"  height="130" />
                  <rect x="552" y="70"  width="62"  height="190" />
                  <rect x="614" y="110" width="36"  height="150" />
                  <rect x="650" y="90"  width="48"  height="170" />
                  <rect x="696" y="140" width="28"  height="120" />
                  <rect x="724" y="120" width="42"  height="140" />
                  <rect x="766" y="150" width="34"  height="110" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          Panel 3 — Latest thinking colourful cards
          ================================================================ */}
      <section
        className="e2-cband e2-cband-news"
        data-testid="e2-cband-news"
        aria-label="Latest thinking"
      >
        <div className="e2-shell">
          <div className="e2-cband-news-head">
            <h3 className="e2-cband-news-heading">Fresh thinking from the team</h3>
            <Link to="/enterprise-2/blog" className="e2-cband-news-more" data-testid="e2-cband-news-more">
              See more on the blog
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          <div className="e2-cband-news-grid">
            {NEWS_CARDS.map((card) => (
              <Link
                key={card.id}
                to={card.to}
                className={`e2-cband-news-card tint-${card.tint}`}
                data-testid={`e2-cband-news-card-${card.id}`}
              >
                <div className="e2-cband-news-thumb" aria-hidden>
                  <div className="a" />
                  <div className="b" />
                  <div className="c" />
                  <div className="grain" />
                </div>
                <div className="e2-cband-news-meta">
                  <span className="e2-cband-news-tag">{card.tag}</span>
                  <span className="e2-cband-news-date">{card.date}</span>
                </div>
                <div className="e2-cband-news-title">{card.title}</div>
                <div className="e2-cband-news-cta">
                  Read more
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          Panel 4 — Green pastoral "your sovereign AI workplace"
          with a floating dark terminal card
          ================================================================ */}
      <section
        className="e2-cband e2-cband-pastoral"
        data-testid="e2-cband-pastoral"
        aria-label="Your sovereign AI workplace"
      >
        <div className="e2-cband-pastoral-bg" aria-hidden>
          <div className="hills" />
          <div className="hills h2" />
          <div className="hills h3" />
          <div className="road" />
          <div className="grain" />
        </div>

        <div className="e2-cband-pastoral-inner">
          <div className="e2-cband-pastoral-lead">
            <h3 className="e2-cband-pastoral-title">
              A workplace where your AI is truly yours
            </h3>
            <p className="e2-cband-pastoral-sub">
              Every model, every agent, every workflow, resident on infrastructure you control. Deployed inside your walls, handed over for your team to own.
            </p>
            <Link
              to="/enterprise-2/solution"
              className="e2-cband-btn e2-cband-btn-white"
              data-testid="e2-cband-pastoral-cta"
            >
              See what we build
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          <div className="e2-cband-terminal" aria-hidden>
            <div className="e2-cband-terminal-head">
              <span className="d" />
              <span className="d" />
              <span className="d" />
              <span className="t">foundation &nbsp;·&nbsp; automations</span>
              <span className="pill">Alpha</span>
            </div>
            <div className="e2-cband-terminal-body">
              <div className="row head">
                <span>Automations</span>
                <span className="link">+ New automation</span>
              </div>
              <div className="row muted">Browse automations that streamline repetitive tasks.</div>
              <div className="tabs">
                <span className="tab on">Discovery</span>
                <span className="tab">Runs</span>
                <span className="tab">My builds</span>
                <span className="tab">Monitor</span>
              </div>
              <div className="cards">
                <div className="card">
                  <div className="ic" />
                  <div className="t">Revenue Scope</div>
                  <div className="s">Contract ingestion pipeline for finance.</div>
                </div>
                <div className="card">
                  <div className="ic" />
                  <div className="t">Meeting Summariser</div>
                  <div className="s">Notes and action items across your calls.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ================================================================
          Panel 5 — Purple grain "Build on Crowther" developer band
          ================================================================ */}
      <section
        className="e2-cband e2-cband-devband"
        data-testid="e2-cband-devband"
        aria-label="Build on Crowther"
      >
        <div className="e2-cband-devband-inner">
          <div className="e2-cband-devband-lead">
            <div className="e2-cband-eyebrow">For engineering teams</div>
            <h3 className="e2-cband-devband-title">Build on Crowther, on your own terms.</h3>
            <p className="e2-cband-devband-sub">
              Ship agents, workflows and evaluations against your own governed platform. Full API, tenant keys, and audit trails that satisfy your regulator.
            </p>
            <div className="e2-cband-devband-actions">
              <Link
                to="/enterprise-2/solution"
                className="e2-cband-btn e2-cband-btn-primary"
                data-testid="e2-cband-devband-primary"
              >
                Read the docs
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link
                to="/enterprise-2/contact"
                className="e2-cband-devband-link"
                data-testid="e2-cband-devband-link"
              >
                Request an API key &nbsp;↗
              </Link>
            </div>
          </div>

          <div className="e2-cband-devband-mock" aria-hidden>
            <div className="e2-cband-devband-mock-inner">
              <div className="e2-cband-devband-mock-head">
                <div className="mark">
                  <span className="dot" />
                  <span className="brand">Crowther docs</span>
                  <span className="ver">v2 API</span>
                </div>
                <div className="tabs">
                  <span className="tab on">Guides and concepts</span>
                  <span className="tab">API Reference</span>
                  <span className="tab">Release Notes</span>
                  <span className="tab">Cookbooks</span>
                </div>
              </div>
              <div className="e2-cband-devband-mock-body">
                <aside className="side">
                  <div className="side-h">Get Started</div>
                  <div className="side-row">Introduction</div>
                  <div className="side-row">Installation</div>
                  <div className="side-row on">Creating a client</div>
                  <div className="side-row chev">Quickstart <span>›</span></div>
                  <div className="side-row">Playground</div>
                  <div className="side-row">FAQs</div>
                  <div className="side-h">Models</div>
                  <div className="side-row">An Overview of Crowther&rsquo;s Models</div>
                  <div className="side-row chev">Atom <span>›</span></div>
                </aside>
                <div className="main">
                  <div className="crumb">Get Started</div>
                  <div className="h1">Creating a client</div>
                  <div className="h2">Creating Crowther API Client</div>
                  <div className="p">To start using all features available in the Crowther SDK, first.</div>
                  <div className="langs">
                    <span className="on">Python</span>
                    <span>TypeScript</span>
                    <span>Java</span>
                    <span>Go</span>
                  </div>
                  <pre className="code">
<span className="ln">1</span> <span className="kw">import</span> crowther{"\n"}
<span className="ln">2</span>{"\n"}
<span className="ln">3</span> co <span className="op">=</span> crowther.<span className="fn">ClientV2</span>(api_key)
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
